import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSession } from '../../context/SessionContext';
import { FEEDBACK_ENDPOINTS } from '../../constants/apiEndpoints';

const Finished = () => {
    const { sessionData } = useSession();
    const params = useParams();
    const eid = params.eid;
    
    // Feedback state
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [hoveredStar, setHoveredStar] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    
    // No longer clearing the session automatically
    // This allows users to refresh the page and still see their completion information

    // Handle feedback submission
    const handleFeedbackSubmit = async () => {
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // Create the data structure to send to the server
            const dataToSend = {
                eval_id: eid,
                rating: rating,
                feedback: feedback
            };
            
            // Log the data being submitted to the server
            console.log('Submitting Feedback Data:', JSON.stringify(dataToSend, null, 2));
            
            // Send the data to the webhook
            const response = await fetch(FEEDBACK_ENDPOINTS.SAVE_FEEDBACK, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText}`);
            }
            
            // Parse the response
            const responseData = await response.json();
            
            // Log successful submission
            console.log('Feedback submitted successfully!', new Date().toISOString());
            console.log('Response:', responseData);
            
            // Update state
            setSubmitted(true);
            
            // Show success toast
            toast.success('Thank you for your feedback!');
            
        } catch (err) {
            console.error('Error submitting feedback:', err);
            // Log detailed error information for debugging
            console.log('Submission Error Details:', {
                timestamp: new Date().toISOString(),
                errorMessage: err.message,
                rating: rating,
                feedback: feedback,
                eid: eid
            });
            
            setError(err.message);
            
            // Show error toast
            toast.error('Failed to submit feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Simple function to parse and render basic markup text into React components
     * 
     * Supported markup format:
     * 
     * # Heading 1             - Creates an <h1> element
     * ## Heading 2            - Creates an <h2> element
     * ### Heading 3           - Creates an <h3> element
     * 
     * - Bullet point                  - Creates a bullet list item
     * * Alternative bullet            - Also creates a bullet list item
     * - [sm] Small text bullet        - Creates a bullet with small text
     * - [md] Medium text bullet       - Creates a bullet with medium text
     * - [lg] Large text bullet        - Creates a bullet with large text
     * 
     * 1. Numbered item                - Creates a numbered list item
     * 2. Another item                 - Creates another numbered list item
     * 1. [sm] Small numbered item     - Creates a numbered item with small text
     * 2. [md] Medium numbered item    - Creates a numbered item with medium text
     * 3. [lg] Large numbered item     - Creates a numbered item with large text
     * 
     * Regular text            - Creates a paragraph
     * 
     * Each line is processed separately, so lists need to be created with
     * consecutive lines using the same format.
     * 
     * Example:
     * ```
     * # Welcome to Your Results
     * 
     * ## What happens next?
     * 
     * - Your evaluation will be reviewed by our team
     * - You will receive feedback within 3-5 business days
     * 
     * 1. Complete any additional steps
     * 2. Prepare for the next round if selected
     * ```
     * 
     * @param {string} text - The markup text to render
     * @returns {Array} Array of React elements
     */
    const renderSimpleMarkup = (text) => {
        if (!text) return null;
        
        // Split the text into lines
        const lines = text.split('\n');
        
        // Process each line and convert to appropriate React elements
        return lines.map((line, index) => {
            // Trim the line
            const trimmedLine = line.trim();
            
            // Skip empty lines
            if (!trimmedLine) return null;
            
            // Check for headings (# Heading 1, ## Heading 2, ### Heading 3)
            if (trimmedLine.startsWith('# ')) {
                return <h1 key={index} className="text-2xl font-bold mb-4">{trimmedLine.substring(2)}</h1>;
            }
            
            if (trimmedLine.startsWith('## ')) {
                return <h2 key={index} className="text-xl font-semibold mb-3">{trimmedLine.substring(3)}</h2>;
            }
            
            if (trimmedLine.startsWith('### ')) {
                return <h3 key={index} className="text-lg font-medium mb-2">{trimmedLine.substring(4)}</h3>;
            }
            
            // Check for bullet points (- Item or * Item) with optional size modifier
            if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
                let content = trimmedLine.substring(2);
                let sizeClass = "";
                
                // Check for size modifiers [sm], [md], [lg]
                if (content.startsWith('[sm] ')) {
                    content = content.substring(5);
                    sizeClass = "text-sm";
                } else if (content.startsWith('[md] ')) {
                    content = content.substring(5);
                    sizeClass = "text-base";
                } else if (content.startsWith('[lg] ')) {
                    content = content.substring(5);
                    sizeClass = "text-lg";
                }
                
                return (
                    <ul key={index} className="list-disc ml-6 mb-2">
                        <li className={sizeClass}>{content}</li>
                    </ul>
                );
            }
            
            // Check for numbered list (1. Item) with optional size modifier
            if (/^\d+\.\s/.test(trimmedLine)) {
                let content = trimmedLine.replace(/^\d+\.\s/, '');
                let sizeClass = "";
                
                // Check for size modifiers [sm], [md], [lg]
                if (content.startsWith('[sm] ')) {
                    content = content.substring(5);
                    sizeClass = "text-sm";
                } else if (content.startsWith('[md] ')) {
                    content = content.substring(5);
                    sizeClass = "text-base";
                } else if (content.startsWith('[lg] ')) {
                    content = content.substring(5);
                    sizeClass = "text-lg";
                }
                
                return (
                    <ol key={index} className="list-decimal ml-6 mb-2">
                        <li className={sizeClass}>{content}</li>
                    </ol>
                );
            }
            
            // Default to paragraph
            return <p key={index} className="mb-3">{trimmedLine}</p>;
        });
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
            <div className="w-full max-w-[800px] bg-white rounded-3xl shadow-md p-12 flex flex-col items-center">
                <div className="text-green-500 text-6xl mb-6">🎉</div>
                <h1 className="text-3xl font-bold mb-4">Evaluation Completed</h1>
                
                {sessionData && (
                    <p className="text-xl mb-6">
                        Thank you, {sessionData.fname}! Your evaluation has been successfully submitted.
                    </p>
                )}
                
                {sessionData && sessionData.evaluation_completed_content ? (
                    <div className="bg-gray-50 p-6 rounded-lg w-full mb-6">
                        {renderSimpleMarkup(sessionData.evaluation_completed_content)}
                    </div>
                ) : (
                    <div className="bg-gray-50 p-6 rounded-lg w-full mb-6">
                        <h2 className="text-xl font-semibold mb-4">What happens next?</h2>
                        <ol className="list-decimal pl-6 space-y-2">
                            <li>The evaluation will be reviewed by the hiring team</li>
                            <li>You will receive feedback within 3-5 business days</li>
                            <li>If selected, you will be invited for the next round of interviews</li>
                        </ol>
                    </div>
                )}
                
                {/* <p className="text-gray-600 italic mb-8">
                    If you have any questions, please contact the hiring team directly.
                </p> */}

                {/* Feedback Section */}
                {!submitted ? (
                    <div className="w-full flex justify-center mt-6">
                        <div className="w-[70%] bg-white border border-gray-400 rounded-lg p-6">
                            <h3 className="text-xl font-medium mb-4 text-center">How did we do?</h3>
                            
                            <div className="mb-6">
                                <div className="flex justify-center space-x-2 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            onMouseEnter={() => setHoveredStar(star)}
                                            onMouseLeave={() => setHoveredStar(0)}
                                            className="focus:outline-none transition-transform hover:scale-110"
                                        >
                                            <svg 
                                                className={`w-8 h-8 ${
                                                    star <= (hoveredStar || rating) 
                                                        ? 'text-yellow-400' 
                                                        : 'text-gray-300'
                                                }`} 
                                                fill="currentColor" 
                                                viewBox="0 0 20 20" 
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="mb-6">
                                <textarea
                                    id="feedback"
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    rows="2"
                                    placeholder="Your feedback greatly helps us improve ..."
                                />
                            </div>
                            
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={handleFeedbackSubmit}
                                    disabled={submitting}
                                    className={`px-6 py-2 border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                                        submitting 
                                            ? 'bg-white border-gray-300 text-gray-400 cursor-not-allowed' 
                                            : 'bg-white border-black text-black hover:bg-gray-50'
                                    }`}
                                >
                                    {submitting ? 'Submitting...' : 'Submit Feedback'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="w-full flex justify-center mt-6">
                        <div className="w-[70%] p-6 text-center">
                            <div className="text-5xl mb-3" style={{ color: '#33C6B6' }}>✓</div>
                            <p className="font-medium text-black text-lg">Thank you for your feedback!</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Finished;
